import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
import {
    Box,
    Stack,
    IconButton,
    Tooltip,
    Divider
} from '@mui/material';

import { IoMdRefresh } from 'react-icons/io';

import { Icon as Iconify } from '@iconify/react';
import ModalPopUp from '../../../commons/ModalPopup';
import { renderControls } from '../../RetailerProductWiseScheme/utils/renderControls';
import PopUpHandle from '../../../commons/PopUpHandle';
import RetailerWiseProductWiseSchemeService from './RetailerWiseProductWiseClaimReportService';
import LoadingProgressBar from '../../../components/loading-progressbar/loadingprogressbar';
import { useAppStore } from '../../../store/appStore';


export default function RetailerWiseProductWiseSchemeFilterSearch({ open, onClose }) {

    const {
        handleSubmit,
        control,
        watch,
        setValue,
        reset

    } = useForm();

    const values = watch();
    const {
        searchControls, setSearchControls,
        setFilterControlValues, filterControlValues,
        setHoverFilterValues, setFilteredData, filteredData,
        setFilterPayload
    } = useAppStore();

    const [defaultSchemeOptions, setDefaultSchemeOptions] = useState([]);
    const watchedSchemeGroupId = watch("SchemeGroupId");
    const watchedSchemeGroup = watch("SchemeGroup");
    const schemeGroup = watchedSchemeGroupId || watchedSchemeGroup;

    const [loading, setLoading] = useState(false)

    const [opene, setOpene] = useState(false);
    const [error, setError] = useState('');
    const [errorCode, setErrorCode] = useState(0);


    const handleClose = () => {
        setOpene(false);
    };


    const isResetting = useRef(false);

    // Default value for the "Report Filter" dropdown — always kept selected
    const DEFAULT_REPORT_FILTER = { ReportFilterId: 1, ReportFilter: "Distributor wise" };
    // console.log('values : ', values);





    const hasInitializedRef = useRef(false);

    useEffect(() => {
        if (!open) {
            hasInitializedRef.current = false;
        }
    }, [open]);

    useEffect(() => {
        if (!open) {
            hasInitializedRef.current = false;
            return;
        }

        if (hasInitializedRef.current) {
            return; // Prevent resetting values when searchControls changes dynamically
        }

        if (searchControls && searchControls.length > 0) {
            isResetting.current = true;
            if (filterControlValues && Object.keys(filterControlValues).length > 0) {
                // Ensure ReportFilterId always has a value when restoring saved filters
                reset({
                    ...filterControlValues,
                    ReportFilterId: filterControlValues.ReportFilterId || DEFAULT_REPORT_FILTER,
                });
            } else {
                // Populate default values from searchControls dynamically
                const defaultVals = { ReportFilterId: DEFAULT_REPORT_FILTER };
                searchControls.forEach(ctrl => {
                    const name = ctrl?.ValueExpr;
                    if (name === "SchemeGroup" || name === "SchemeGroupId") {
                        return; // Skip setting initial value for Scheme Group to avoid auto API call
                    }
                    if (name && ctrl.DefaultValues !== null && ctrl.DefaultValues !== undefined) {
                        if (ctrl.DataType === "RadioButton") {
                            const defVal = Array.isArray(ctrl.DefaultValues) ? ctrl.DefaultValues[0] : ctrl.DefaultValues;
                            defaultVals[name] = defVal;
                        } else if (ctrl.DataType === "MultiselectDropdown") {
                            if (name === "SchemeName") {
                                defaultVals[name] = []; // Do not pre-select all schemes by default
                            } else {
                                defaultVals[name] = Array.isArray(ctrl.DefaultValues) ? ctrl.DefaultValues : [];
                            }
                        } else if (ctrl.DataType === "Dropdown") {
                            defaultVals[name] = Array.isArray(ctrl.DefaultValues) ? ctrl.DefaultValues[0] : ctrl.DefaultValues;
                        } else {
                            defaultVals[name] = ctrl.DefaultValues;
                        }
                    }
                });
                reset(defaultVals); // Reset to defaults
            }
            hasInitializedRef.current = true;
            setTimeout(() => {
                isResetting.current = false;
            }, 100);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, searchControls]);

    // Guard: if the user clears the Report Filter dropdown, snap it back to the default
    const reportFilterValue = watch('ReportFilterId');
    useEffect(() => {
        if (!reportFilterValue && !isResetting.current) {
            setValue('ReportFilterId', DEFAULT_REPORT_FILTER);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportFilterValue]);



    const [serviceInstance, setServiceInstance] = useState(null);

    useEffect(() => {
        const setInstance = async () => {
            const instance = await RetailerWiseProductWiseSchemeService;
            setServiceInstance(instance);
        };
        setInstance();
    }, []);

    useEffect(() => {
        if (searchControls.length > 0 && defaultSchemeOptions.length === 0) {
            const schemeControl = searchControls.find(f => f.ValueExpr === "SchemeId" || f.ValueExpr === "SchemeName");
            if (schemeControl) {
                setDefaultSchemeOptions(schemeControl.DefaultValues || []);
            }
        }
    }, [searchControls, defaultSchemeOptions.length]);

    useEffect(() => {
        const updateSchemeOptions = async () => {
            const groupId = schemeGroup?.Id || schemeGroup?.SchemeGroupId || (typeof schemeGroup === 'number' || typeof schemeGroup === 'string' ? schemeGroup : null);

            if (!groupId) {
                // Restore default options
                if (defaultSchemeOptions.length > 0) {
                    const newData = [...searchControls];
                    const schemeControlIndex = newData.findIndex(f => f.ValueExpr === "SchemeId" || f.ValueExpr === "SchemeName");
                    if (schemeControlIndex !== -1) {
                        newData[schemeControlIndex] = {
                            ...newData[schemeControlIndex],
                            DefaultValues: defaultSchemeOptions
                        };
                        setSearchControls(newData);
                    }
                }
                setValue("SchemeName", []);
                return;
            }

            try {
                setLoading(true);
                if (serviceInstance) {
                    const schemes = await serviceInstance.getSchemesByGroupId(groupId, true);
                    if (schemes) {
                        const parsedSchemes = typeof schemes === 'string' ? JSON.parse(schemes) : schemes;

                        // Read DataType BEFORE updating state
                        const schemeControl = searchControls.find(f => f.ValueExpr === "SchemeId" || f.ValueExpr === "SchemeName");
                        const schemeDataType = schemeControl?.DataType;

                        const newData = [...searchControls];
                        const schemeControlIndex = newData.findIndex(f => f.ValueExpr === "SchemeId" || f.ValueExpr === "SchemeName");
                        if (schemeControlIndex !== -1) {
                            newData[schemeControlIndex] = {
                                ...newData[schemeControlIndex],
                                DefaultValues: parsedSchemes
                            };
                            setSearchControls(newData);
                        }

                        // Auto-select: array for MultiselectDropdown, single object for Dropdown
                        if (schemeDataType === "MultiselectDropdown") {
                            setValue("SchemeName", parsedSchemes);
                        } else {
                            setValue("SchemeName", parsedSchemes.length === 1 ? parsedSchemes[0] : null);
                        }
                    }
                    
                }
            } catch (err) {
                console.error("Error updating scheme options:", err);
                setLoading(false);
            } finally {
                setLoading(false);
            }
        };

        updateSchemeOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schemeGroup, serviceInstance, defaultSchemeOptions, setSearchControls, setValue]);

    const onSubmit = async (data) => {
        // console.log('searchControls', searchControls);
        // console.log('data', data);

        //  Resolve ShowDetails once (no ternary nesting)
        const showDetailsValue = data?.ShowDetails === true ? "1" : "0";


        setFilterControlValues(data);

        try {
            setLoading(true);

            const schemeConversionFilterServiceInstance =
                await RetailerWiseProductWiseSchemeService;

            if (!schemeConversionFilterServiceInstance) {
                throw new Error("Service instance could not be created");
            }

            //  ALWAYS-BUILT PAYLOAD (fixed structure)
            // Start - Logic to handle "Select All" or "Empty Selection" as "All Schemes"
            const schemeControl = searchControls?.find(ctrl => ctrl?.DataType === "MultiselectDropdown");
            const totalSchemes = schemeControl?.DefaultValues?.length || 0;
            const selectedSchemes = data?.SchemeName || [];

            const distributorControl = searchControls?.find(
                ctrl => ctrl?.DataType === "MultiselectDropdown" && ctrl?.ValueExpr === "DistributorId"
            );

            const totalDistributors = distributorControl?.DefaultValues?.length || 0;
            const selectedDistributors = data?.DistributorId || [];

            const payload = {
                SchemeIds: selectedSchemes.map(s => s?.SchemeId) || []
            };

            const recCat = data?.RecordCategoriesId || data?.RecordCategories;
            if (recCat !== undefined && recCat !== null) {
                payload.RecordCategories = String(typeof recCat === 'object' ? (recCat.RecordCategoriesId ?? recCat.Id ?? "") : recCat);
            }



      /*      const payload = {
                SchemeIds: (selectedSchemes?.length > 0 && selectedSchemes?.length < totalSchemes)
                    ? selectedSchemes.map(s => s?.SchemeId)
                    : [],
                DistributorIds: (selectedDistributors?.length > 0 && selectedDistributors?.length < totalDistributors)
                    ? selectedDistributors.map(d => d?.DistributorId)
                    : [],
                FromDate: data?.FromDate || "",
                ToDate: data?.ToDate || "",
                Description: data?.Description || "",
                ReportFilterId: (data?.ReportFilterId?.ReportFilterId ?? DEFAULT_REPORT_FILTER.ReportFilterId) || 1

            };
*/
            setHoverFilterValues(data);
            setFilterPayload(payload);

            const response =
                await schemeConversionFilterServiceInstance
                    .GetRetailerProductClaimReport(payload);

            setFilteredData(response?.data || []);

            onClose();
        } catch (err) {
            console.log('error', err);

            setErrorCode(err?.response?.data?.error?.code);
            setError(err?.response?.data?.error?.message);
            setOpene(true);

            if (err?.response?.data?.error?.code === 404) {
                setFilteredData([]);
            }
        } finally {
            setLoading(false);
        }
    };




    const handleReset = () => {
        const defaultVals = {
            SchemeName: [],
            DistributorId: [],
            FromDate: "",
            ToDate: "",
            Description: "",
            ShowDetails: 0,
            ReportFilterId: DEFAULT_REPORT_FILTER, // Always reset to default
        };
        if (searchControls && searchControls.length > 0) {
            searchControls.forEach(ctrl => {
                const name = ctrl?.ValueExpr;
                if (name === "SchemeGroup" || name === "SchemeGroupId") {
                    return;
                }
                if (name && ctrl.DefaultValues !== null && ctrl.DefaultValues !== undefined) {
                    if (ctrl.DataType === "RadioButton") {
                        defaultVals[name] = Array.isArray(ctrl.DefaultValues) ? ctrl.DefaultValues[0] : ctrl.DefaultValues;
                    } else if (ctrl.DataType === "Dropdown") {
                        defaultVals[name] = Array.isArray(ctrl.DefaultValues) ? ctrl.DefaultValues[0] : ctrl.DefaultValues;
                    }
                }
            });
        }
        reset(defaultVals);
    };








    return (


        // eslint-disable-next-line react/jsx-boolean-value
        <ModalPopUp open={open} onClose={onClose} title='Filter'
        // sx={{ width: { xs: 320, sm: 520, md: 680 } }}
        >
            <form onSubmit={handleSubmit(onSubmit)}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    minHeight: 0,

                }}
            >
                <Stack spacing={2} sx={{
                    pb: 1.3, pt: 0.1, display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    minHeight: 0,
                    overflow: 'hidden' // Prevent overflow in the Stack
                }}>
                    <Box
                        sx={{
                            overflow: "visible", // instead of "auto"
                            rowGap: 2,
                            columnGap: 2,
                            display: 'grid',
                            // gridTemplateColumns='repeat(4, 1fr)'
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: '1fr',
                                md: '1fr 1fr',
                                lg: '1fr 1fr ',
                                xl: '1fr 1fr 1fr'
                            },
                            gridAutoRows: 'min-content',
                            paddingTop: '0.4rem',
                            // border: '2px solid red',
                        }}
                    >
                        {searchControls?.map((field, index) => renderControls(field, index, control, serviceInstance?.SearchFilter))}

                    </Box>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Tooltip title='Reset' sx={{ bgcolor: '#fdd835', color: '#fff', borderRadius: '4px', height: "34px", width: "34px", padding: "5px" }}>
                            <IconButton onClick={handleReset}>
                                <IoMdRefresh />
                            </IconButton>
                        </Tooltip>
                        <Tooltip
                            title='Apply'
                            sx={{ bgcolor: '#43b5a2', color: '#fff', borderRadius: '4px', marginLeft: '8px', height: "34px", width: "34px", padding: "5px" }}
                        >
                            <IconButton type='submit'>
                                <Iconify icon='mdi:filter' />
                            </IconButton>
                        </Tooltip>
                    </Box>

                </Stack>
            </form>
            {loading && <LoadingProgressBar />}
            {opene && <PopUpHandle open={opene} handleClose={handleClose} error={error} errorCode={errorCode} />}
        </ModalPopUp>

    );

}

RetailerWiseProductWiseSchemeFilterSearch.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
};